import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Send,
  Eye,
  PenTool,
  CheckCircle,
  XCircle,
  Ban,
  Upload,
  Download,
  ClipboardList,
  Mail,
  ChevronLeft,
  ChevronRight,
  FileEdit,
  Activity as ActivityIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";
import { usageApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";

const activityIcons = {
  envelope_created: FileEdit,
  envelope_sent: Send,
  envelope_viewed: Eye,
  envelope_signed: PenTool,
  envelope_completed: CheckCircle,
  envelope_declined: XCircle,
  envelope_voided: Ban,
  document_uploaded: Upload,
  document_downloaded: Download,
  template_created: ClipboardList,
  signature_applied: PenTool,
  email_sent: Mail,
  default: FileText,
};

const activityColors = {
  envelope_created: "bg-blue-500 text-white",
  envelope_sent: "bg-purple-500 text-white",
  envelope_viewed: "bg-slate-500 text-white",
  envelope_signed: "bg-emerald-500 text-white",
  envelope_completed: "bg-emerald-500 text-white",
  envelope_declined: "bg-red-500 text-white",
  envelope_voided: "bg-rose-500 text-white",
  document_uploaded: "bg-blue-500 text-white",
  document_downloaded: "bg-sky-500 text-white",
  template_created: "bg-purple-500 text-white",
  signature_applied: "bg-emerald-500 text-white",
  email_sent: "bg-primary text-primary-foreground",
  default: "bg-slate-500 text-white",
};

const ActivityItem = ({ activity, onClick }) => {
  const { t } = useTranslation();
  const Icon = activityIcons[activity.type] || activityIcons.default;
  const colorClass = activityColors[activity.type] || activityColors.default;

  const getLabel = () => {
    const labels = {
      envelope_created: t("activity.types.envelope_created"),
      envelope_sent: t("activity.types.envelope_sent"),
      envelope_viewed: t("activity.types.envelope_viewed"),
      envelope_signed: t("activity.types.envelope_signed"),
      envelope_completed: t("activity.types.envelope_completed"),
      envelope_declined: t("activity.types.envelope_declined"),
      envelope_voided: t("activity.types.envelope_voided"),
      document_uploaded: t("activity.types.document_uploaded"),
      document_downloaded: t("activity.types.document_downloaded"),
      template_created: t("activity.types.template_created"),
      signature_applied: t("activity.types.signature_applied"),
      email_sent: t("activity.types.email_sent"),
    };
    return labels[activity.type] || activity.type.replace(/_/g, " ");
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return t("activity.time.justNow");
    if (diff < 3600000) {
      return t("activity.time.minutesAgo", { count: Math.floor(diff / 60000) });
    }
    if (diff < 86400000) {
      return t("activity.time.hoursAgo", { count: Math.floor(diff / 3600000) });
    }
    if (diff < 604800000) {
      return t("activity.time.daysAgo", { count: Math.floor(diff / 86400000) });
    }
    const { user } = useAuthStore.getState();
    return formatDateShort(d, user);
  };

  return (
    <div
      className={cn(
        "flex items-start gap-4 p-4 hover:bg-muted/50 transition-colors",
        "active:bg-muted/70",
        onClick && "cursor-pointer",
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full shrink-0",
          colorClass,
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium">{getLabel()}</p>
          {activity.entityName && (
            <Badge variant="outline" className="truncate max-w-50">
              {activity.entityName}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">
          {activity.description || activity.details}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {formatDate(activity.date)}
          {activity.userName && ` · by ${activity.userName}`}
        </p>
      </div>
    </div>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  isFetching,
  isMobile,
}) => {
  const { t } = useTranslation();

  if (isMobile) {
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <Button
          variant="outline"
          size="mobile"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isFetching}
          className="flex-1 mr-2"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          {t("common.previous")}
        </Button>
        <span className="text-sm text-muted-foreground px-2">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="mobile"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || isFetching}
          className="flex-1 ml-2"
        >
          {t("common.next")}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1 || isFetching}
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        Previous
      </Button>
      <span className="text-sm text-muted-foreground">
        Page {currentPage} of {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || isFetching}
      >
        Next
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
};

const ActivitySkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
    </div>
    <Card>
      <CardContent className="p-0">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="flex items-start gap-4 p-4 border-b last:border-0"
          >
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-full max-w-md" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  </div>
);

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <ActivityIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">
          {t("activity.noActivity")}
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          {t("activity.noActivityDesc")}
        </p>
      </CardContent>
    </Card>
  );
};

export const Activity = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);

  const {
    data: activitiesData,
    isLoading: loading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["activities", page],
    queryFn: async () => {
      const response = await usageApi.getAllActivity(page, 20);
      const data = response.data?.data || response.data;
      return {
        activities: data?.activities || [],
        pagination: data?.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
        },
      };
    },
    placeholderData: (previousData) => previousData,
  });

  const activities = activitiesData?.activities || [];
  const pagination = activitiesData?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  };

  const handlePageChange = (newPage) => setPage(newPage);
  const handleActivityClick = (activity) => {
    if (activity.entityType === "envelope" && activity.entityId) {
      navigate(`/envelopes/${activity.entityId}`);
    }
  };
  const handleRefresh = async () => {
    await refetch();
  };

  if (loading) return <ActivitySkeleton />;

  const content = (
    <>
      {activities.length === 0 ? (
        <EmptyState />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {activities.map((activity, index) => (
                <ActivityItem
                  key={activity.id || index}
                  activity={activity}
                  onClick={
                    activity.entityType === "envelope" && activity.entityId
                      ? () => handleActivityClick(activity)
                      : undefined
                  }
                />
              ))}
            </div>
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetching}
                isMobile={isMobile}
              />
            )}
          </CardContent>
        </Card>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-heading tracking-tight">
          {t("activity.title")}
        </h1>
        <p className="text-muted-foreground">{t("activity.subtitle")}</p>
      </div>
      {isMobile ? (
        <PullToRefresh onRefresh={handleRefresh}>{content}</PullToRefresh>
      ) : (
        content
      )}
    </div>
  );
};

export default Activity;
